/**
 * @fileoverview Invoice Status Badge Component
 * @task B.INV.5 - Accounts Receivable invoice screens
 *
 * Displays invoice status with appropriate styling
 */

import { cn } from '@lib/utils';

export type InvoiceStatus = 'draft' | 'sent' | 'viewed' | 'partial' | 'paid' | 'overdue' | 'void';

interface InvoiceStatusBadgeProps {
  status: InvoiceStatus;
  className?: string;
  isBrazil?: boolean;
}

const icons = {
  draft: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  sent: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  ),
  viewed: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  partial: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  paid: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  overdue: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  void: (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  ),
};

const statusConfigEN: Record<InvoiceStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Sent', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  viewed: { label: 'Viewed', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  partial: { label: 'Partial', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'Paid', className: 'bg-green-50 text-green-700 border-green-200' },
  overdue: { label: 'Overdue', className: 'bg-red-50 text-red-700 border-red-200' },
  void: { label: 'Void', className: 'bg-gray-100 text-gray-500 border-gray-300 line-through' },
};

const statusConfigBR: Record<InvoiceStatus, { label: string; className: string }> = {
  draft: { label: 'Rascunho', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Enviado', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  viewed: { label: 'Visualizado', className: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  partial: { label: 'Parcial', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'Pago', className: 'bg-green-50 text-green-700 border-green-200' },
  overdue: { label: 'Vencido', className: 'bg-red-50 text-red-700 border-red-200' },
  void: { label: 'Cancelado', className: 'bg-gray-100 text-gray-500 border-gray-300 line-through' },
};

export function InvoiceStatusBadge({ status, className, isBrazil = false }: InvoiceStatusBadgeProps): JSX.Element {
  const statusConfig = isBrazil ? statusConfigBR : statusConfigEN;
  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        config.className,
        className
      )}
    >
      {icons[status]}
      {config.label}
    </span>
  );
}
